import type {ResourceTone} from '@/constants/resourceTone';

/**
 * Where the *drawable* part of each stat bar actually sits inside its element.
 *
 * The bars are art. Each `.rpg-stat-bar` is a 15rem box carrying a border PNG,
 * and its wrapper is masked by a companion PNG whose opaque region is the
 * channel the fill may occupy. That channel is neither the full width of the
 * box nor the same on all three bars — the frames have end caps of different
 * sizes, and `background-size: contain` then scales each art to a different
 * width depending on its own aspect ratio.
 *
 * A percentage taken against the box is therefore not a percentage of the bar.
 * Action Points is the worst case: the channel ends at 69.1%, so a fill of 75%
 * and a fill of 100% are the same picture, and anything drawn past 69.1% — a
 * small cost's shadow, say — is simply invisible.
 *
 * HOW THESE WERE MEASURED, and how to redo it if the art is ever replaced:
 * take the mask PNG, read the alpha along its vertical midline, and note the
 * first and last columns with alpha > 16. Convert those columns to a fraction
 * of the element using the `contain` fit its bar declares — fit by width when
 * the art is proportionally wider than the box, otherwise by height, then
 * centre the result, since the mask is positioned `center`.
 *
 *   mask                                    img       midline opaque
 *   halth_bar_with_border_mask_invented    1061x197   x 88..860
 *   flow_bar_with_border_mask_invented     1061x152   x 31..808
 *   action_points_mask_inverted            1061x178   x 35..733
 *
 * Box sizes are 15rem wide by 2.8 / 2.12 / 2.5rem tall; the fit depends only on
 * aspect ratios, so these percentages hold at any root font size.
 */
export interface BarChannel {
  /** Percentage of the element where the drawable channel begins. */
  start: number;
  /** Percentage of the element where it ends. */
  end: number;
}

export const STAT_BAR_CHANNEL: Record<ResourceTone, BarChannel> = {
  health: {start: 8.29, end: 81.15},
  flow: {start: 3.56, end: 75.9},
  actionPoints: {start: 3.61, end: 69.05},
};

/**
 * A 0..1 share of a resource as a percentage of the element — the number a
 * width or an offset can actually be set to.
 *
 * Everything that draws on a bar goes through this, so the fill's right edge
 * and the shadow's waterline cannot drift apart.
 */
export function channelPercent(channel: BarChannel, fraction: number): number {
  const clamped = Math.min(1, Math.max(0, Number.isFinite(fraction) ? fraction : 0));
  return channel.start + clamped * (channel.end - channel.start);
}

/** A resource's share of its pool, guarding the empty-pool case. */
export function shareOf(current: number, max: number): number {
  return max > 0 ? current / max : 0;
}
